import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import { isAdmin } from '../PolicyConfig/utils/adminAccess';
import { fetchSSOConfig, getDefaultConfig, parseSAMLMetadata, SSOConfigApiError, saveSSOConfig, validateConfig } from './api';
import { AlertMessage } from './components';
import { EmailPasswordSection, GoogleOAuthSection, GoogleSAMLSection, MicrosoftOAuthSection, MicrosoftSAMLSection } from './sections';
const TENANT_SLUG = 'default';
// Support both port-based (staging) and domain-based (sprezz) deployments
const getAuthServiceUrl = ()=>{
    const origin = window.location.origin;
    // For domain-based deployments: agents.sprezz.specra.ai → auth.sprezz.specra.ai
    if (origin.includes('agents.sprezz.specra.ai')) {
        return origin.replace('agents.sprezz.specra.ai', 'auth.sprezz.specra.ai');
    }
    // For domain-based deployments: agents.staging.specra.ai → auth.staging.specra.ai
    if (origin.includes('agents.staging.specra.ai')) {
        return origin.replace('agents.staging.specra.ai', 'auth.staging.specra.ai');
    }
    // For port-based deployments: localhost:3001 → localhost:9091
    return origin.replace(':3001', ':9091');
};
const AUTH_SERVICE_URL = getAuthServiceUrl();
export default function SSOConfigPage() {
    const [config, setConfig] = useState(getDefaultConfig());
    const [secrets, setSecrets] = useState({
        google_client_secret: '',
        microsoft_client_secret: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isUploadingXML, setIsUploadingXML] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    // Same as Settings: GetSSOConfig is admin-only, and this route is not
    // admin-gated either.
    const { user } = useAuth();
    const canManageSSO = isAdmin(user);
    const handleFetchConfig = useCallback(async ()=>{
        if (!canManageSSO) {
            setConfig(getDefaultConfig());
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await fetchSSOConfig();
            setConfig(data);
        } catch (err) {
            if (err instanceof SSOConfigApiError && err.status === 404) {
                setConfig(getDefaultConfig());
            } else {
                setError(err instanceof Error ? err.message : 'Failed to fetch SSO configuration');
            }
        } finally{
            setLoading(false);
        }
    }, [
        canManageSSO
    ]);
    const handleSaveConfig = async ()=>{
        setLoading(true);
        setError(null);
        setSuccess(null);
        const validationError = validateConfig(config, secrets);
        if (validationError) {
            setError(validationError);
            setLoading(false);
            return;
        }
        try {
            const payload = {
                ...config,
                google_client_secret: secrets.google_client_secret || undefined,
                microsoft_client_secret: secrets.microsoft_client_secret || undefined
            };
            const savedConfig = await saveSSOConfig(payload);
            setConfig(savedConfig);
            setSuccess('SSO configuration saved successfully!');
            // Clear secrets after successful save
            setSecrets({
                google_client_secret: '',
                microsoft_client_secret: ''
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save SSO configuration');
        } finally{
            setLoading(false);
        }
    };
    const handleMicrosoftXMLUpload = async (event)=>{
        const file = event.target.files?.[0];
        if (!file) return;
        setIsUploadingXML(true);
        setUploadError(null);
        try {
            const text = await file.text();
            const parsed = await parseSAMLMetadata(text, 'microsoft');
            // Directly set the config with all fields
            setConfig({
                ...config,
                microsoft_saml_entity_id: parsed.entity_id,
                microsoft_saml_sso_url: parsed.sso_url,
                microsoft_saml_certificate: parsed.certificate
            });
        } catch (err) {
            setUploadError(err instanceof Error ? err.message : 'Failed to parse metadata file');
        } finally{
            setIsUploadingXML(false);
            event.target.value = '';
        }
    };
    useEffect(()=>{
        handleFetchConfig();
    }, [
        handleFetchConfig
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Helmet, {
                children: [
                    /*#__PURE__*/ _jsx("title", {
                        children: "SSO Configuration - GovCon AI"
                    }),
                    /*#__PURE__*/ _jsx("meta", {
                        name: "description",
                        content: "Configure Single Sign-On authentication providers"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "p-6",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col lg:flex-row gap-8",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "lg:w-64 flex-shrink-0",
                            children: /*#__PURE__*/ _jsx("nav", {
                                className: "space-y-1",
                                children: /*#__PURE__*/ _jsx(Link, {
                                    to: "/settings",
                                    className: "w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors bg-indigo-100 text-indigo-700",
                                    children: "← Back to Settings"
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "bg-white rounded-lg shadow-sm border border-gray-200 p-6",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mb-6",
                                        children: [
                                            /*#__PURE__*/ _jsx("h1", {
                                                className: "text-3xl font-bold text-gray-900",
                                                children: "SSO Configuration"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-gray-600 mt-1",
                                                children: "Configure Single Sign-On authentication providers for your organization plz"
                                            })
                                        ]
                                    }),
                                    error && /*#__PURE__*/ _jsx(AlertMessage, {
                                        variant: "error",
                                        children: error
                                    }),
                                    success && /*#__PURE__*/ _jsx(AlertMessage, {
                                        variant: "success",
                                        children: success
                                    }),
                                    uploadError && /*#__PURE__*/ _jsx(AlertMessage, {
                                        variant: "error",
                                        children: uploadError
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-6",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "bg-white rounded-lg border border-gray-200 p-6",
                                                children: /*#__PURE__*/ _jsx("div", {
                                                    className: "flex items-start justify-between",
                                                    children: /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("h3", {
                                                                className: "text-lg font-semibold text-gray-900 mb-1",
                                                                children: "📁 Upload Microsoft SAML XML"
                                                            }),
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "text-sm text-gray-600 mb-4",
                                                                children: "Quick setup: Upload your Federation Metadata XML from Azure AD to automatically configure Microsoft SAML"
                                                            }),
                                                            /*#__PURE__*/ _jsx("label", {
                                                                htmlFor: "microsoft-metadata-upload",
                                                                className: `inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium cursor-pointer transition-colors ${isUploadingXML ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed' : 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'}`,
                                                                children: isUploadingXML ? 'Uploading...' : 'Choose XML File'
                                                            }),
                                                            /*#__PURE__*/ _jsx("input", {
                                                                id: "microsoft-metadata-upload",
                                                                type: "file",
                                                                accept: ".xml,text/xml,application/xml",
                                                                onChange: handleMicrosoftXMLUpload,
                                                                disabled: isUploadingXML,
                                                                className: "hidden"
                                                            }),
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "mt-3 text-xs text-gray-500",
                                                                children: "Download from: Azure Portal → Enterprise Applications → Single sign-on → SAML Certificates → Federation Metadata XML"
                                                            })
                                                        ]
                                                    })
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(EmailPasswordSection, {
                                                enabled: config.email_password_enabled,
                                                onToggle: (enabled)=>setConfig({
                                                        ...config,
                                                        email_password_enabled: enabled
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsx(GoogleOAuthSection, {
                                                enabled: config.google_enabled,
                                                clientId: config.google_client_id,
                                                clientSecret: secrets.google_client_secret,
                                                onToggle: (enabled)=>setConfig({
                                                        ...config,
                                                        google_enabled: enabled
                                                    }),
                                                onClientIdChange: (value)=>setConfig({
                                                        ...config,
                                                        google_client_id: value
                                                    }),
                                                onClientSecretChange: (value)=>setSecrets({
                                                        ...secrets,
                                                        google_client_secret: value
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsx(MicrosoftOAuthSection, {
                                                enabled: config.microsoft_enabled,
                                                clientId: config.microsoft_client_id,
                                                clientSecret: secrets.microsoft_client_secret,
                                                tenantId: config.microsoft_tenant_id,
                                                onToggle: (enabled)=>setConfig({
                                                        ...config,
                                                        microsoft_enabled: enabled
                                                    }),
                                                onClientIdChange: (value)=>setConfig({
                                                        ...config,
                                                        microsoft_client_id: value
                                                    }),
                                                onClientSecretChange: (value)=>setSecrets({
                                                        ...secrets,
                                                        microsoft_client_secret: value
                                                    }),
                                                onTenantIdChange: (value)=>setConfig({
                                                        ...config,
                                                        microsoft_tenant_id: value
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsx(GoogleSAMLSection, {
                                                enabled: config.google_saml_enabled || false,
                                                entityId: config.google_saml_entity_id || '',
                                                ssoUrl: config.google_saml_sso_url || '',
                                                certificate: config.google_saml_certificate || '',
                                                emailAttribute: config.google_saml_email_attribute || 'email',
                                                authServiceUrl: AUTH_SERVICE_URL,
                                                tenantSlug: TENANT_SLUG,
                                                onToggle: (enabled)=>setConfig({
                                                        ...config,
                                                        google_saml_enabled: enabled
                                                    }),
                                                onEntityIdChange: (value)=>setConfig({
                                                        ...config,
                                                        google_saml_entity_id: value
                                                    }),
                                                onSsoUrlChange: (value)=>setConfig({
                                                        ...config,
                                                        google_saml_sso_url: value
                                                    }),
                                                onCertificateChange: (value)=>setConfig({
                                                        ...config,
                                                        google_saml_certificate: value
                                                    }),
                                                onEmailAttributeChange: (value)=>setConfig({
                                                        ...config,
                                                        google_saml_email_attribute: value
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsx(MicrosoftSAMLSection, {
                                                enabled: config.microsoft_saml_enabled || false,
                                                entityId: config.microsoft_saml_entity_id || '',
                                                ssoUrl: config.microsoft_saml_sso_url || '',
                                                certificate: config.microsoft_saml_certificate || '',
                                                emailAttribute: config.microsoft_saml_email_attribute || '',
                                                authServiceUrl: AUTH_SERVICE_URL,
                                                tenantSlug: TENANT_SLUG,
                                                onToggle: (enabled)=>setConfig((prev)=>({
                                                            ...prev,
                                                            microsoft_saml_enabled: enabled
                                                        })),
                                                onEntityIdChange: (value)=>setConfig((prev)=>({
                                                            ...prev,
                                                            microsoft_saml_entity_id: value
                                                        })),
                                                onSsoUrlChange: (value)=>setConfig((prev)=>({
                                                            ...prev,
                                                            microsoft_saml_sso_url: value
                                                        })),
                                                onCertificateChange: (value)=>setConfig((prev)=>({
                                                            ...prev,
                                                            microsoft_saml_certificate: value
                                                        })),
                                                onEmailAttributeChange: (value)=>setConfig((prev)=>({
                                                            ...prev,
                                                            microsoft_saml_email_attribute: value
                                                        }))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "bg-indigo-50 border border-indigo-200 rounded-md p-4 mt-6",
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "text-indigo-800",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "font-medium",
                                                    children: "💡 Configuration Notes"
                                                }),
                                                /*#__PURE__*/ _jsxs("ul", {
                                                    className: "mt-2 text-sm space-y-1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("li", {
                                                            children: "• Changes take effect immediately after saving"
                                                        }),
                                                        /*#__PURE__*/ _jsx("li", {
                                                            children: "• At least one authentication method must be enabled"
                                                        }),
                                                        /*#__PURE__*/ _jsx("li", {
                                                            children: "• OAuth redirect URLs must be configured in provider consoles"
                                                        }),
                                                        /*#__PURE__*/ _jsx("li", {
                                                            children: "• Test SSO configuration before deploying to production"
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-6 flex justify-end space-x-3",
                                        children: [
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: handleFetchConfig,
                                                disabled: loading,
                                                className: "px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors",
                                                children: "Reset"
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: handleSaveConfig,
                                                disabled: loading,
                                                className: "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors",
                                                children: loading ? 'Saving...' : 'Save Changes'
                                            })
                                        ]
                                    })
                                ]
                            })
                        })
                    ]
                })
            })
        ]
    });
}
